import { computed, Injectable, signal } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { Order, OrderCoverage } from '../models/order.entity'
import { Coverage } from '../models/order.types'
import { OrdersRepository } from '../orders.repository'
import { Product, ProductsService } from '../../products'
import {
  OrdersListingData,
  OrdersListingPage,
} from '../models/order-listing.model'
import { OrderField, OrderSearchParams } from '../models/order-search.model'
import { parseViewStateFiltersOptions, View } from '../../views'
import { OrdersService } from '../orders.service'
import { defaultIfEmpty, firstValueFrom, Observable, of, take } from 'rxjs'
import { values, merge, keyBy, omit, union, difference, uniq } from 'lodash'
import saveAs from 'file-saver'
import {
  PickingListOrder,
  PickingListsService,
  PickingUnusableProduct,
} from '../../picking-lists'
import { WarehousesService } from '../../warehouses'
import { CurrenciesService } from '../../currencies'
import { PaymentsService } from '../../payments'
import { CountriesService } from '../../countries'
import { CarriersService } from '../../carriers'
import { UsersService } from '../../users'
import { ReasonsService } from '../../reasons'
import { Tag, TagsService } from '../../tags'
import { ChannelsService } from '../../channels'
import { COVERAGE_TYPES, ORDER_SYSTEM_TAGS } from '../consts/order.const'
import { mergeById } from '../../../libs/filter.lib'
import { TranslationService } from '@evologi/shared/util-translation'

export interface OrdersListingState {
  data?: Order[]
  extData?: OrdersListingData
  dropdownOptions?: OrdersListingData
  totalCount?: number
  searchString?: string
  fields?: OrderField[]
  filters?: OrderSearchParams
  searchKeys?: OrderSearchParams
  customFilters?: OrderSearchParams
  trackingNumber?: string
  coverage: PickingListOrder[]
  unusable: PickingUnusableProduct[]
  coverages?: (Order & OrderCoverage)[]
  coverageCounters?: Record<Coverage, number>
  coverageProducts?: Product[]
  productTags?: Tag[]
  error?: HttpErrorResponse
  toReload: boolean
  isLoading: boolean
  isInitialized: boolean
  view?: View
}

export interface OrdersFilterData {
  params?: OrderSearchParams
  count?: number
}

interface CoverageData {
  coverage: PickingListOrder[]
  unusable: PickingUnusableProduct[] | undefined
}

const ORDERS_INITIAL_STATE: OrdersListingState = {
  coverage: [],
  unusable: [],
  isLoading: false,
  isInitialized: false,
  toReload: false,
}

@Injectable()
export class OrdersListingStore {
  constructor(
    private ordersRepository: OrdersRepository,
    private ordersService: OrdersService,
    private productsService: ProductsService,
    private pickingListsService: PickingListsService,
    private warehousesService: WarehousesService,
    private currenciesService: CurrenciesService,
    private paymentsService: PaymentsService,
    private countriesService: CountriesService,
    private channelsService: ChannelsService,
    private carriersService: CarriersService,
    private usersService: UsersService,
    private reasonsService: ReasonsService,
    private tagsService: TagsService,
    private translationService: TranslationService,
  ) {}

  private state = signal<OrdersListingState>(ORDERS_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly orders = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)
  readonly fields = computed(() => this.state().fields)
  readonly trackingNumber = computed(() => this.state().trackingNumber)
  readonly customFilters = computed(() => this.state().customFilters)
  readonly view = computed(() => this.state().view)
  readonly coverage = computed(() => this.state().coverage)
  readonly unusable = computed(() => this.state().unusable)
  readonly coverages = computed(() => this.state().coverages)
  readonly coverageCounters = computed(() => this.state().coverageCounters)
  readonly coverageProducts = computed(() => this.state().coverageProducts)
  readonly productTags = computed(() => this.state().productTags)

  // Effects

  async search(
    searchParams: {
      params?: OrderSearchParams
      returnAll?: boolean
      inventoryCoverage?: boolean
      warehouseId?: string
    },
    removeShipmentsNotRequested?: boolean,
    pickingOrderIds?: string[],
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersRepository
        .searchOrders$(
          this.parseStateFilterParams(this.state(), searchParams.params),
          this.state().fields ?? [],
          this.state().extData,
          searchParams.returnAll,
        )
        .subscribe({
          next: async (data) => {
            let orders = data.data ?? []

            if (removeShipmentsNotRequested) {
              orders = orders.filter((order) =>
                order.shipments?.some((s) => s.status === 'REQUESTED'),
              )
            }

            if (pickingOrderIds?.length) {
              orders = orders.filter(
                (order) => !pickingOrderIds.includes(order._id),
              )
            }

            const fixedData: OrdersListingPage = {
              ...data,
              data: orders,
            }

            let coverageData: CoverageData | undefined
            if (
              searchParams.inventoryCoverage &&
              searchParams.warehouseId &&
              fixedData.data &&
              fixedData.data.length > 0
            ) {
              coverageData = await this.loadCoverage(
                fixedData.data,
                searchParams.warehouseId,
              )
            }
            const productTags = await this.loadProductTags()
            this.setData(fixedData, coverageData, productTags)
            resolve()
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async searchCoverage(searchParams: {
    params?: OrderSearchParams
    returnAll?: boolean
    warehouseId?: string
  }): Promise<void> {
    if (!searchParams.warehouseId) {
      this.ordersRepository.notify({ code: 'WAREHOUSE_NOT_FOUND' })
      return
    }

    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersService.list$(searchParams.params, true).subscribe({
        next: async (data) => {
          let counters: Record<Coverage, number> = {
            ['PL-ASSIGNED']: 0,
            ['AVAILABLE']: 0,
            ['UNAVAILABLE']: 0,
            ['PARTIAL']: 0,
          }
          const orderIds = data.map((ord) => ord._id)
          const coverages = await this.getMultiCoverage(
            searchParams.warehouseId!,
            orderIds,
            data,
          )
          if (coverages) {
            counters = coverages.reduce((counters, order) => {
              counters[order.coverage]++
              return counters
            }, counters)
          }
          this.setCoverageData(coverages, counters)
          resolve()
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async getMultiCoverage(
    warehouseId: string,
    orderIds: string[],
    orders: Order[],
  ): Promise<(Order & OrderCoverage)[] | undefined> {
    return new Promise((resolve, reject) => {
      this.ordersService.multiCoverage$(warehouseId, orderIds).subscribe({
        next(value) {
          const coverages: (Order & OrderCoverage)[] = values(
            merge(keyBy(orders, '_id'), keyBy(value, '_id')),
          )
          const parsedOrders = coverages.map((order) => {
            order.pickingListId && (order.coverage = 'PL-ASSIGNED')
            return order
          })
          resolve(parsedOrders)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async searchOrderProducts(order: Order): Promise<void> {
    if (!order.rows.length) {
      return
    }
    const coverageProducts = this.coverageProducts()

    let productIds = order.rows.map((r) => r.product._id)
    productIds = difference(
      productIds,
      (coverageProducts ?? []).map((prod) => prod._id),
    )

    this.unsetError()
    return new Promise((resolve, reject) => {
      this.productsService
        .list$({
          _id: uniq(productIds),
          fields: [
            '_id',
            'SKU',
            'productType',
            'onHandQty',
            'availableQty',
            'incomingQty',
            'receivingQty',
            'isDropShip',
          ],
        })
        .subscribe({
          next: (value) => {
            this.setCoverageProducts(
              uniq([...(coverageProducts ?? []), ...value]),
            )
            resolve()
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async loadOrderProducts(orderId: string): Promise<void> {
    const products = this.extData()?.products
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersRepository.getOrderProducts$(orderId, products).subscribe({
        next: (data) => {
          resolve()
          this.setProducts(data)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async loadCoverage(
    orders: Order[],
    warehouseId: string,
  ): Promise<CoverageData | undefined> {
    try {
      const result = await firstValueFrom(
        this.pickingListsService
          .simulate$(
            warehouseId,
            orders.map((o) => ({ _id: o._id })),
          )
          .pipe(defaultIfEmpty(undefined)),
      )
      if (!result) {
        return
      }

      return {
        coverage: result.orders,
        unusable: result.unusable,
      }
    } catch {
      return
    }
  }

  async loadProductTags(): Promise<Tag[] | undefined> {
    try {
      const result = await firstValueFrom(
        this.tagsService
          .list$({ target: 'products' }, true)
          .pipe(defaultIfEmpty(undefined)),
      )
      if (!result) {
        return
      }

      return result
    } catch {
      return
    }
  }

  async searchPickingList(goodsReceiveId: string): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.ordersRepository.getPickingListIds$(goodsReceiveId).subscribe({
        next: (data) => {
          resolve()
          this.setSearchKeys({ _id: data })
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async duplicateOrder(order: Order) {
    return new Promise<boolean>((resolve) => {
      this.ordersRepository.alert('DUPLICATE_ORDER', order).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.ordersService.duplicate$(order._id).subscribe({
            next: () => {
              this.ordersRepository.notify({
                code: 'ORDER_DUPLICATED',
              })
              this.setIsLoading(false)
              resolve(true)
            },
            error: (error: HttpErrorResponse) => {
              this.setError(error)
              resolve(false)
            },
          })
        }
      })
    })
  }

  async delete(order: Order): Promise<boolean> {
    if (order.attachments?.length) {
      this.ordersRepository.notify({ code: 'ATTACHMENTS_FOUND' })
      return false
    }

    return new Promise<boolean>((resolve) => {
      this.ordersRepository.alert('ORDER_DELETION', order).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.ordersService.delete$(order._id).subscribe({
            next: () => {
              resolve(true)
              this.ordersRepository.notify({
                code: 'ORDER_DELETED',
              })
              this.setIsLoading(false)
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof OrdersListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<NonNullable<OrdersListingData[LookupKey]>>

    const queryKey = `${event.dataDisplayField ?? 'name'}:ct`
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      coverage: () =>
        of(
          COVERAGE_TYPES.map((item) => ({
            ...item,
            name: this.translationService.translate(item.name),
          })),
        ),
      warehouses: () => this.warehousesService.list$(query),
      currencies: () => this.currenciesService.list$(query),
      payments: () => this.paymentsService.list$(query),
      countries: () => this.countriesService.list$(query),
      channels: () => this.channelsService.list$(query),
      carriers: () => this.carriersService.list$(query),
      users: () => this.usersService.list$(query),
      pickingLists: () => this.pickingListsService.list$(query),
      products: () => this.productsService.list$(query),
      reasons: () => this.reasonsService.list$(query),
      tags: () => this.tagsService.list$({ ...query, target: 'orders' }),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      const normalizedItems =
        event.dataSourceKey === 'tags'
          ? this.mergeOrderSystemTags(items as Tag[], event.q)
          : items
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = normalizedItems as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  private mergeOrderSystemTags(tags: Tag[] = [], q?: string): Tag[] {
    const term = (q ?? '').trim().toLowerCase()

    const now = new Date()
    const systemTags: Tag[] = ORDER_SYSTEM_TAGS.filter(
      (value) => !term || value.toLowerCase().includes(term),
    ).map((value) => ({
      _id: `system:${value}`,
      target: 'orders',
      value,
      tenantId: tags.length > 0 ? tags[0].tenantId : '',
      createdAt: now.toISOString(),
      createdBy: now.toISOString(),
      updatedAt: now.toISOString(),
      updatedBy: now.toISOString(),
    }))

    const byValue = new Map<string, Tag>()

    // Keep API tags when same value exists, but ensure system ones are present
    for (const tag of [...systemTags, ...tags]) {
      if (tag?.value) byValue.set(tag.value, tag)
    }

    return Array.from(byValue.values())
  }

  downloadSummary(order: Order) {
    return new Promise((resolve, reject) => {
      this.ordersService
        .downloadSummary$(order._id)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            resolve(response)
            const blob = response.body

            if (blob) {
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `riepilogo-${order.header.orderNumber}.pdf`
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              a.remove()
            }
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  downloadDdt(order: Order) {
    const shippedId = order.shipments?.find((s) => s.status === 'SHIPPED')?._id
    const requestedId = order.shipments?.find(
      (s) => s.status === 'REQUESTED',
    )?._id
    if (!shippedId && !requestedId) {
      this.ordersRepository.notify({ code: 'SHIPMENT_NOT_FOUND' })
    }

    return new Promise((resolve, reject) => {
      this.ordersService
        .downloadDdt$(order._id, shippedId ?? requestedId ?? '')
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            resolve(response)
            const blob = response.body

            if (blob) {
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `ddt-${order.header.orderNumber}.pdf`
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              a.remove()
            }
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  downloadLabel(order: Order) {
    return new Promise((resolve, reject) => {
      this.ordersRepository.downloadOrderLabel$(order).subscribe({
        next: (response) => {
          if (!response || !Array.isArray(response)) {
            this.ordersRepository.notify({
              code: 'LABEL_NOT_FOUND',
            })
            return
          }

          resolve(response)
          response.forEach((blob, index) => {
            if (blob) {
              if (
                `${blob.type}`.includes('zpl') ||
                `${blob.body?.type}`.includes('zpl')
              ) {
                blob.body?.text().then((zplString: string) => {
                  this.ordersRepository
                    .downloadPdfLabel(zplString)
                    .subscribe((l) => {
                      const url = window.URL.createObjectURL(l)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `etichetta-ordine-${order.header.orderNumber}-${index + 1}.pdf`
                      document.body.appendChild(a)
                      a.click()
                      window.URL.revokeObjectURL(url)
                      a.remove()
                    })
                })
              } else {
                const response = blob.body
                if (response) {
                  const url = window.URL.createObjectURL(response)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = `etichetta-ordine-${order.header.orderNumber}-${index + 1}.pdf`
                  document.body.appendChild(a)
                  a.click()
                  window.URL.revokeObjectURL(url)
                  a.remove()
                }
              }
            }
          })
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  downloadInvoice(order: Order) {
    let shipmentId: string | undefined
    order.shipments?.map((s) => {
      if (!shipmentId) {
        if (s.status === 'SHIPPED' || s.status === 'REQUESTED') {
          shipmentId = s._id
        }
      }
    })
    if (!shipmentId) {
      this.ordersRepository.notify({ code: 'SHIPMENT_MISSING' })
      return
    }

    return new Promise((resolve, reject) => {
      this.ordersService
        .downloadInvoice$(order._id, shipmentId!)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            resolve(response)
            const blob = response.body

            if (blob) {
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = `ddt-${order.header.orderNumber}.pdf`
              document.body.appendChild(a)
              a.click()
              window.URL.revokeObjectURL(url)
              a.remove()
            }
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async downloadUploadTemplate() {
    this.setIsLoading(true)
    return new Promise((resolve, reject) => {
      this.ordersService.downloadOldOrdersUploadTemplate$().subscribe({
        next: (response) => {
          resolve(response)
          this.setIsLoading(false)
          if (response.body) {
            saveAs(response.body, `orders-template.xlsx`)
          }
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  // Reducers

  setData(
    data: OrdersListingPage,
    coverageData?: CoverageData,
    productTags?: Tag[],
  ) {
    const coverage = this.coverage()
    const unusable = this.unusable()
    const currentTags = this.productTags()
    this.state.set({
      ...this.state(),
      coverage: union(coverage, coverageData?.coverage),
      unusable: union(unusable, coverageData?.unusable),
      data: data.data,
      totalCount: data.totalCount,
      extData: data.extData,
      productTags: productTags ?? currentTags,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setCoverageData(
    coverages?: (Order & OrderCoverage)[],
    counters?: Record<Coverage, number>,
  ) {
    this.state.set({
      ...this.state(),
      coverages,
      coverageCounters: counters,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setCoverageProducts(products: Product[]) {
    this.state.set({
      ...this.state(),
      coverageProducts: products,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: OrderSearchParams, keepFilters?: boolean) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters:
        keepFilters !== true
          ? { ...filters }
          : { ...this.filters(), ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: OrderSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  addCustomFilters(filter: OrderSearchParams) {
    this.state.set({
      ...this.state(),
      customFilters: { ...this.state().customFilters, ...filter },
    })
  }

  removeFilter(filter: OrderSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof OrderSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: OrderField[]) {
    this.state.set({ ...this.state(), fields })
  }

  setSearchKeys(searchKeys?: OrderSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      searchString: undefined,
      trackingNumber: undefined,
      filters: {},
    })
  }

  setCustomFilters(customFilters?: OrderSearchParams) {
    this.state.set({
      ...this.state(),
      customFilters,
    })
  }

  setProducts(products: Product[]) {
    const extData = this.extData()

    this.state.set({
      ...this.state(),
      extData: {
        ...(extData || {}),
        products,
      },
      isLoading: false,
    })
  }

  setTrackingNumber(trackingNumber: string | undefined) {
    this.state.set({
      ...this.state(),
      trackingNumber,
    })
  }

  setTotalCount(totalCount: number) {
    this.state.set({
      ...this.state(),
      totalCount,
    })
  }

  setView(view: View) {
    this.state.set({
      ...this.state(),
      view,
      fields: view.fields as OrderField[],
      customFilters: !view.filters
        ? {}
        : parseViewStateFiltersOptions<OrderSearchParams>(view.filters, {}),
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
      coverage: [],
      unusable: [],
    })
  }

  private parseStateFilterParams(
    state: OrdersListingState,
    filters?: OrderSearchParams,
  ): OrderSearchParams {
    let params: OrderSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.trackingNumber) {
      params.trackingNumber = state.trackingNumber
    }
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }
    if (filters?.sort) {
      params.sort = filters.sort
    }

    params = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => key !== undefined && value !== undefined,
      ),
    )

    return params
  }

  filterData(selected: Order[] | undefined) {
    const state = this.state()

    if (selected && selected.length > 0) {
      return {
        params: {
          _id: selected.map((o) => o._id),
        },
        count: selected.length,
      }
    }

    return {
      params: omit(this.parseStateFilterParams(state), [
        'limit',
        'offset',
        'sort',
        'order',
      ]),
      count: this.totalCount(),
    }
  }
}
